import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NuvoModal } from '@nuvolo/nuux-core/components/NuvoModal';
import type { Item } from '../../hooks/useDashboardItems';
import ConfigList from './ConfigList';
import TMMButton from '#/common/components/TMMButton';

export interface DashboardConfigProps {
  visible: boolean;
  onHide: () => void;
  items: Item[];
  onSave: (newItems: Item[]) => void;
  defaultItems: Item[];
  defaultItemsJson: string;
}

const DashboardConfig: React.FC<DashboardConfigProps> = ({
  visible,
  items: originalItems,
  onSave,
  onHide,
  defaultItems,
  defaultItemsJson,
}) => {
  const [items, setItems] = useState([...originalItems]);

  useEffect(() => {
    setItems([...originalItems]);
  }, [originalItems]);

  const sameAsDefaults = useMemo(() => {
    const itemsJson = JSON.stringify(items);
    return itemsJson === defaultItemsJson;
  }, [items, defaultItemsJson]);

  const sameAsOriginal = useMemo(() => {
    const itemsJson = JSON.stringify(items);
    const originalsJson = JSON.stringify(originalItems);
    return itemsJson === originalsJson;
  }, [items, originalItems]);

  const resetToDefault = useCallback(() => {
    setItems(defaultItems);
  }, [defaultItems]);

  const cancel = useCallback(() => {
    setItems(originalItems);
    onHide();
  }, [onHide, originalItems]);

  const save = useCallback(() => {
    onSave(items);
    onHide();
  }, [items, onHide, onSave]);

  return (
    <NuvoModal
      visible={visible}
      title="Configure Dashboard"
      dragEnabled
      resizeEnabled
      minWidth={400}
      maxWidth={600}
      minHeight={400}
      maxHeight={600}
      content={<ConfigList items={items} onChange={setItems} />}
      actions={[
        <TMMButton
          key="revert"
          label="Back to Default"
          onClick={resetToDefault}
          disabled={sameAsDefaults}
          type="secondary"
        />,
        <TMMButton key="cancel" label="Cancel" onClick={cancel} type="normal" />,
        <TMMButton
          key="save"
          label="Save"
          onClick={save}
          disabled={sameAsOriginal}
          type="submit"
        />,
      ]}
      onHidden={cancel}
    />
  );
};

export default React.memo(DashboardConfig);
